import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const CookiePolicy = () => {
  const lastUpdated = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="container mx-auto py-12 px-4 sm:px-6 lg:px-8 max-w-5xl">
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-400 bg-clip-text text-transparent mb-4">
          Cookie Policy
        </h1>
        <p className="text-gray-500 text-lg max-w-3xl mx-auto">
          Last updated: {lastUpdated}
        </p>
        <Separator className="my-8" />
      </div>

      {/* Introduction */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="introduction">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Introduction</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            Digika.ai (“<strong>Digika</strong>”, “<strong>we</strong>”, “<strong>us</strong>”, or “<strong>our</strong>”) uses cookies and similar technologies on our websites and platform (the “<strong>Services</strong>”). This Cookie Policy explains what cookies are, the types we use, why we use them, and how you can manage your preferences.
          </p>
          <p>
            Where required by law, we obtain your consent for non-essential cookies via our cookie banner and settings. For more information on how we process personal data, please see our Privacy Policy.
          </p>
        </CardContent>
      </Card>

      {/* What Are Cookies */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="what-are-cookies">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">What Are Cookies?</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            Cookies are small text files placed on your device (computer, tablet, mobile) when you visit a website. They are widely used to make websites work, to improve user experience, and to provide information to site operators.
          </p>
          <p>
            We also use related technologies such as local storage, pixels, tags, and SDKs (“<strong>trackers</strong>”). In this Policy, we refer to all of these collectively as “cookies”.
          </p>
        </CardContent>
      </Card>

      {/* Types of Cookies */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="types">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Types of Cookies We Use</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-700">Essential (Strictly Necessary)</h3>
            <p>
              Required for core functionality and security (e.g., session management, authentication, fraud prevention, cookie consent storage). These cannot be switched off in our systems.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-700">Preferences</h3>
            <p>
              Remember your choices (e.g., language, region, UI settings) to personalize your experience.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-700">Analytics & Performance</h3>
            <p>
              Help us understand usage of the Services (page views, navigation patterns) so we can improve features. Examples include privacy-friendly analytics or tools configured via our tag manager.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-700">Marketing</h3>
            <p>
              Used (with consent) to deliver relevant content or measure campaign effectiveness. These may be set by us or by carefully selected third parties.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-700">Payments & Integrations</h3>
            <p>
              When you use regulated payment service providers (e.g., PayPal Business, Binance Pay), they may set cookies necessary to process transactions securely under their own policies.
            </p>
          </div>

          <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
            <h3 className="text-lg font-semibold text-gray-800">Web3 / Wallet Connections (if enabled)</h3>
            <p className="text-gray-600 mt-1">
              If you opt to connect a crypto wallet in the future, we may use local storage or similar to remember your connection state and network. Blockchain transactions are public by design and may be immutable.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Third-Party Cookies */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="third-parties">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Third-Party Cookies</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            We may use third-party providers for analytics, payments, communications, security, and (where applicable) marketing. These providers may set cookies according to their own policies. Examples include:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Tag managers and analytics tools for usage measurement;</li>
            <li>Payment processors for secure transaction handling;</li>
            <li>Customer support and messaging widgets;</li>
            <li>Social media integrations for sharing or sign-in;</li>
            <li>Security and anti-fraud services.</li>
          </ul>
          <p className="text-sm text-gray-500">
            We aim to minimize personal data sharing and configure tools in line with privacy best practices.
          </p>
        </CardContent>
      </Card>

      {/* Managing Preferences */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="manage">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Managing Your Cookie Preferences</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            On your first visit, our cookie banner lets you accept or reject non-essential categories. You can also change your choices at any time via the banner’s settings (e.g., “Manage Cookies” or “Cookie Settings” link in the footer).
          </p>
          <h3 className="text-xl font-semibold text-gray-700 mt-2">Browser Controls</h3>
          <p>
            Most browsers allow you to view, delete, or block cookies. If you block certain cookies, parts of the Services may not function properly.
          </p>
          <h3 className="text-xl font-semibold text-gray-700 mt-2">Do Not Track</h3>
          <p>
            “Do Not Track” signals are not standardized; we currently do not respond to them. You can instead manage your preferences using our banner and browser settings.
          </p>
        </CardContent>
      </Card>

      {/* Cookie Lifespan */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="lifespan">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Cookie Lifespan</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            Some cookies are session-based (deleted when you close your browser). Others are persistent and remain for a defined period (e.g., days to months) to recognize your device on return visits. Exact durations vary by cookie and provider.
          </p>
        </CardContent>
      </Card>

      {/* Changes */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="changes">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Changes to This Cookie Policy</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            We may update this Policy to reflect changes in technology, regulation, or our practices. We will post updates here and adjust the “Last updated” date above. Where appropriate, we will provide additional notice (e.g., banner or in-app).
          </p>
        </CardContent>
      </Card>

      {/* Contact */}
      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm" id="contact">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Contact Us</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            Questions about this Cookie Policy or your choices? Contact our privacy team:
          </p>
          <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100 mt-2">
            <p><strong>Email (Privacy):</strong> <a className="text-blue-600 hover:underline" href="mailto:privacy@digika.ai">privacy@digika.ai</a></p>
            <p className="mt-1"><strong>Support:</strong> <a className="text-blue-600 hover:underline" href="mailto:hello@digika.ai">hello@digika.ai</a></p>
          </div>
        </CardContent>
      </Card>

      <div className="text-center mt-12 text-sm text-gray-500">
        <p>© {new Date().getFullYear()} Digika.ai. All rights reserved.</p>
      </div>
    </div>
  );
};

export default CookiePolicy;
